<template>
  <div id="main-page">
    <div class="container">
      <app-bar
        title="Detail Pendaftaran"
        previous-link="/event/saya" />

      <Loading v-if="isLoading" />

      <template v-else-if="myEventDetail">
        <!-- Banner -->
        <div class="event-banner">
          <img :src="myEventDetail.acara?.gambar_url" :alt="myEventDetail.acara?.judul" />
          <span class="badge-harga" :class="myEventDetail.acara?.is_gratis === 't' ? 'badge-gratis' : 'badge-bayar'">
            {{ myEventDetail.acara?.is_gratis === 't' ? 'GRATIS' : formatHarga(myEventDetail.acara?.harga) }}
          </span>
        </div>

        <!-- Event Info Card — same structure as Detail.vue -->
        <div class="section-card mt-3">
          <h4 class="fw-700 mb-3">{{ myEventDetail.acara?.judul }}</h4>

          <div class="event-meta-row mb-2" v-if="myEventDetail.acara?.tgl_mulai && isSameDay(myEventDetail.acara?.tgl_mulai, myEventDetail.acara?.tgl_selesai)">
            <div class="meta-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#007a98" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
            </div>
            <div>
              <div class="meta-label">Tanggal</div>
              <div class="meta-value">
                {{ formatDate(myEventDetail.acara?.tgl_mulai) }} · {{ formatTime(myEventDetail.acara?.tgl_mulai) }}
                <template v-if="myEventDetail.acara?.tgl_selesai"> – {{ formatTime(myEventDetail.acara?.tgl_selesai) }}</template>
                WIB
              </div>
            </div>
          </div>

          <template v-else-if="myEventDetail.acara?.tgl_mulai">
            <div class="event-meta-row mb-2">
              <div class="meta-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#007a98" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              </div>
              <div>
                <div class="meta-label">Mulai</div>
                <div class="meta-value">{{ formatTanggal(myEventDetail.acara?.tgl_mulai) }}</div>
              </div>
            </div>
            <div class="event-meta-row mb-2" v-if="myEventDetail.acara?.tgl_selesai">
              <div class="meta-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#007a98" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
              </div>
              <div>
                <div class="meta-label">Selesai</div>
                <div class="meta-value">{{ formatTanggal(myEventDetail.acara?.tgl_selesai) }}</div>
              </div>
            </div>
          </template>

          <div class="event-meta-row mb-2" v-if="myEventDetail.acara?.lokasi">
            <div class="meta-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#007a98" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 10c0 7-9 13-9 13S3 17 3 10a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>
            </div>
            <div>
              <div class="meta-label">Lokasi</div>
              <div class="meta-value">{{ myEventDetail.acara?.lokasi }}</div>
            </div>
          </div>
        </div>

        <!-- Description — same as Detail.vue -->
        <div class="section-card mt-3" v-if="eventDescription">
          <h6 class="section-title fw-700">Deskripsi</h6>
          <p class="mb-2 text-muted fs-13">{{ eventDescription }}</p>
        </div>

        <!-- Ticket / Registration Info -->
        <div class="section-card mt-3">
          <h6 class="section-title fw-700">Tiket Saya</h6>

          <div class="detail-item" v-if="myEventDetail.nama_pasien">
            <div class="detail-item__label">Peserta</div>
            <div class="detail-item__value fw-600">{{ myEventDetail.nama_pasien }}</div>
          </div>

          <div class="detail-item">
            <div class="detail-item__label">No. Peserta</div>
            <div class="detail-item__value fw-600 font-mono">{{ myEventDetail.nomor_peserta }}</div>
          </div>

          <div class="detail-item">
            <div class="detail-item__label">No. Urut Peserta</div>
            <div class="detail-item__value fw-600 font-mono">{{ myEventDetail.nomor_urut_peserta }}</div>
          </div>

          <div class="detail-item">
            <div class="detail-item__label">Status</div>
            <div class="detail-item__value d-flex gap-2 flex-wrap">
              <span class="status-badge" :class="myEventDetail.status_daftar === 'TERDAFTAR' ? 'status-badge--terdaftar' : 'status-badge--pending'">
                <span class="status-badge__dot"></span>
                {{ myEventDetail.status_daftar }}
              </span>
              <span v-if="myEventDetail.status_bayar_nama" class="status-badge" :class="myEventDetail.status_bayar_nama === 'LUNAS' ? 'status-badge--lunas' : 'status-badge--belum-bayar'">
                <span class="status-badge__dot"></span>
                {{ myEventDetail.status_bayar_nama?.replace(/_/g, ' ') }}
              </span>
            </div>
          </div>

          <div class="detail-item" v-if="myEventDetail.acara?.is_gratis === 'f'">
            <div class="detail-item__label">Harga</div>
            <div class="detail-item__value fw-600">{{ formatHarga(myEventDetail.acara?.harga) }}</div>
          </div>
        </div>

        <!-- Check-in / Check-out — always shown -->
        <div class="section-card mt-3">
          <h6 class="section-title fw-700">Kehadiran Event</h6>
          <p class="mb-2 text-muted fs-13">Scan QR Code di meja pendaftaran untuk merekam kedatangan dan kepulangan Anda.</p>
          <div class="checkin-timeline">
            <!-- Check-in -->
            <div class="checkin-row">
              <div class="checkin-icon" :class="myEventDetail.kehadiran?.check_in_at ? 'checkin-icon--in' : 'checkin-icon--empty'">
                <svg v-if="myEventDetail.kehadiran?.check_in_at" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              </div>
              <div>
                <div class="checkin-label">Check-in</div>
                <div class="checkin-time">
                  {{ myEventDetail.kehadiran?.check_in_at ? formatTanggal(myEventDetail.kehadiran.check_in_at) : 'Belum check-in' }}
                </div>
              </div>
            </div>
            <!-- Check-out -->
            <div class="checkin-row">
              <div class="checkin-icon" :class="myEventDetail.kehadiran?.check_out_at ? 'checkin-icon--out' : 'checkin-icon--empty'">
                <svg v-if="myEventDetail.kehadiran?.check_out_at" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
                <svg v-else width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
              </div>
              <div>
                <div class="checkin-label">Check-out</div>
                <div class="checkin-time">
                  {{ myEventDetail.kehadiran?.check_out_at ? formatTanggal(myEventDetail.kehadiran.check_out_at) : 'Belum check-out' }}
                </div>
              </div>
            </div>
          </div>
          <button v-if="!myEventDetail.kehadiran?.check_out_at" class="btn-scan-booth mt-3" @click="openScanner('kehadiran')" :disabled="scanLoading">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
            {{ myEventDetail.kehadiran?.check_in_at ? 'Check Out' : 'Check In' }}
          </button>
        </div>

        <!-- Booth List — same as Detail.vue -->
        <div class="section-card mt-3" v-if="eventBooths.length">
          <h6 class="section-title fw-700">Kunjungan Booth</h6>
          <p class="mb-2 text-muted fs-13">Kunjungi booth di lokasi acara, lakukan scan QR untuk mendapatkan poin dan merchandise.</p>
          <div class="booth-list">
            <div class="booth-item" v-for="booth in eventBooths" :key="booth.id">
              <div class="booth-icon" :class="booth.tgl_scan ? 'booth-icon--scanned' : ''">
                <svg v-if="booth.tgl_scan" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                <svg v-else width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#007a98" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
              </div>
              <div>
                <span>{{ booth.nama }}</span>
                <div v-if="booth.tgl_scan" class="checkin-time">{{ formatDate(booth.tgl_scan) }} · {{ booth.jam_scan?.slice(0, 5) }} WIB</div>
              </div>
            </div>
          </div>
          <button class="btn-scan-booth mt-3" @click="openScanner" :disabled="scanLoading">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
            Scan Booth Baru
          </button>
        </div>

        <!-- QR Scanner Overlay -->
        <div v-if="showScanner" class="scanner-overlay">
          <div class="scanner-header">
            <span class="scanner-title">{{ scannerMode === 'kehadiran' ? 'Scan QR Kehadiran' : 'Scan QR Booth' }}</span>
            <button class="scanner-close" @click="closeScanner">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
          </div>
          <div class="scanner-viewport">
            <video ref="scannerVideo" class="scanner-video" playsinline></video>
            <div class="scanner-frame">
              <div class="scanner-corner scanner-corner--tl"></div>
              <div class="scanner-corner scanner-corner--tr"></div>
              <div class="scanner-corner scanner-corner--bl"></div>
              <div class="scanner-corner scanner-corner--br"></div>
            </div>
            <canvas ref="scannerCanvas" style="display:none;"></canvas>
          </div>
          <p class="scanner-hint">{{ scannerMode === 'kehadiran' ? 'Arahkan kamera ke QR code di meja pendaftaran' : 'Arahkan kamera ke QR code booth' }}</p>
          <div v-if="scanLoading" class="scanner-loading">
            <div class="spinner-border spinner-border-sm text-light me-2"></div>
            Memproses...
          </div>
        </div>

        <div class="pb-5"></div>
      </template>

      <!-- Not found -->
      <div
        v-else
        class="not-found d-flex flex-column text-center pb-5 pt-5">
        <div class="bd-highlight">
          <img src="@/assets/icon/not-found.png" alt="">
        </div>
        <p class="text-title-active fm-poppins fst-normal fw-600 fs-24 lh-36">Tidak Ada Hasil</p>
        <p class="text-body-light fm-nunito fst-normal fw-400 fs-16 lh-21">Detail pendaftaran event tidak ditemukan.</p>
      </div>
    </div>
  </div>
</template>

<script>
import AppBar from '@/components/AppBar'
import Loading from '@/components/utility/Loading'
import { computed, nextTick, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import { title } from '@/core/page'
import { getMyEventDetail, scanBooth, scanKehadiran, myEventDetail, loading } from '@/core/event'
import { showNotif } from '@/core/utility'
import jsqr from 'jsqr'

export default {
  components: {
    AppBar, Loading
  },
  setup () {
    const route = useRoute()
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des']

    const eventDescription = computed(() => myEventDetail.value?.acara?.deskripsi || null)
    const eventBooths = computed(() => myEventDetail.value?.booth || [])

    const isLoading = computed(() => loading.getMyEventDetail)

    const parseDate = (tgl) => new Date(tgl.replace(' ', 'T'))

    const formatDate = (tgl) => {
      if (!tgl) return ''
      const date = parseDate(tgl)
      return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`
    }

    const formatTime = (tgl) => {
      if (!tgl) return ''
      const date = parseDate(tgl)
      return `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`
    }

    const formatTanggal = (tgl) => {
      if (!tgl) return ''
      return `${formatDate(tgl)} · ${formatTime(tgl)} WIB`
    }

    const isSameDay = (tgl1, tgl2) => {
      if (!tgl1 || !tgl2) return false
      const d1 = parseDate(tgl1)
      const d2 = parseDate(tgl2)
      return d1.getFullYear() === d2.getFullYear() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getDate() === d2.getDate()
    }

    const formatHarga = (harga) => {
      if (!harga || harga === '0') return 'GRATIS'
      return 'Rp ' + Number(harga).toLocaleString('id-ID')
    }

    onMounted(async () => {
      const { acara_id, pas_id_periksa } = route.query
      if (acara_id && pas_id_periksa) {
        await getMyEventDetail(acara_id, pas_id_periksa)
      }

      nextTick(() => {
        title.value = 'Detail Pendaftaran'
      })
    })

    // QR Scanner
    const showScanner = ref(false)
    const scanLoading = ref(false)
    const scannerMode = ref('booth') // 'booth' | 'kehadiran'
    const scannerVideo = ref(null)
    const scannerCanvas = ref(null)
    let cameraStream = null
    let scanAnimFrame = null

    const stopCamera = () => {
      if (scanAnimFrame) {
        cancelAnimationFrame(scanAnimFrame)
        scanAnimFrame = null
      }
      if (cameraStream) {
        cameraStream.getTracks().forEach(t => t.stop())
        cameraStream = null
      }
    }

    const closeScanner = () => {
      stopCamera()
      showScanner.value = false
      scanLoading.value = false
    }

    const openScanner = async (mode = 'booth') => {
      scannerMode.value = mode
      showScanner.value = true
      await nextTick()
      try {
        cameraStream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } })
        const video = scannerVideo.value
        video.srcObject = cameraStream
        video.play()

        const canvas = document.createElement('canvas')
        const ctx = canvas.getContext('2d')

        const tick = async () => {
          if (!showScanner.value || scanLoading.value) return
          const w = video.videoWidth
          const h = video.videoHeight
          if (w && h) {
            canvas.width = w
            canvas.height = h
            ctx.drawImage(video, 0, 0, w, h)
            const imageData = ctx.getImageData(0, 0, w, h)
            const code = jsqr(imageData.data, imageData.width, imageData.height)
            if (code) {
              scanLoading.value = true
              stopCamera()
              try {
                const pasId = myEventDetail.value?.pas_id_periksa
                if (scannerMode.value === 'kehadiran') {
                  const result = await scanKehadiran(pasId, code.data)
                  if (result.status) {
                    showNotif({ status: 'success', message: result.message || 'Kehadiran berhasil direkam' })
                    const { acara_id, pas_id_periksa } = route.query
                    await getMyEventDetail(acara_id, pas_id_periksa)
                  }
                } else {
                  const result = await scanBooth(pasId, code.data)
                  if (result.status) {
                    showNotif({ status: 'success', message: result.message || 'Scan booth berhasil' })
                    const { acara_id, pas_id_periksa } = route.query
                    await getMyEventDetail(acara_id, pas_id_periksa)
                  }
                }
              } catch (_) {
                // error already shown by the API function
              } finally {
                closeScanner()
              }
              return
            }
          }
          scanAnimFrame = requestAnimationFrame(tick)
        }

        scanAnimFrame = requestAnimationFrame(tick)
      } catch (err) {
        showNotif({ status: 'error', message: 'Tidak bisa mengakses kamera' })
        closeScanner()
      }
    }

    return {
      isLoading,
      myEventDetail,
      eventDescription,
      eventBooths,
      formatDate,
      formatTime,
      formatTanggal,
      formatHarga,
      isSameDay,
      showScanner,
      scanLoading,
      scannerMode,
      scannerVideo,
      scannerCanvas,
      openScanner,
      closeScanner
    }
  }
}
</script>

<style lang="scss" scoped>
$primary: #007A98;
$primary-light: #EBFFFD;
$green: #1CB242;
$green-light: #e8f8ec;
$orange: #FF9D0A;
$orange-light: #fff5e6;
$red: #FD5252;
$red-light: #fef0f0;
$blue: #3566FC;
$blue-light: #eef2ff;
$neutral-400: #CBD5E0;
$neutral-500: #A0AEC0;
$neutral-600: #718096;

.event-banner {
  position: relative;
  margin: 0 -12px;

  img {
    width: 100%;
    display: block;
    object-fit: contain;
    height: auto;
  }

  .badge-harga {
    position: absolute;
    top: 12px;
    left: 12px;
    font-size: 12px;
    font-weight: 600;
    padding: 5px 14px;
    border-radius: 20px;
    line-height: 1.4;

    &.badge-gratis {
      background-color: #198754;
      color: #fff;
    }

    &.badge-bayar {
      background-color: #fff;
      color: $primary;
      box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
    }
  }
}

.section-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 8px rgba(0, 0, 0, 0.08);
  padding: 16px;

  h4 {
    font-size: 20px;
    line-height: 28px;
    color: #1A202C;
  }
}

.section-title {
  font-size: 16px;
  color: #1A202C;
  margin-bottom: 12px;
}

.section-body {
  font-size: 14px;
  line-height: 1.7;
  white-space: pre-line;
}

.event-meta-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;

  .meta-icon {
    flex-shrink: 0;
    width: 36px;
    height: 36px;
    background-color: #E6F4F8;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .meta-label {
    font-size: 12px;
    color: #6c757d;
    line-height: 1.4;
  }

  .meta-value {
    font-size: 14px;
    font-weight: 600;
    color: #1A202C;
    line-height: 1.5;
  }
}

// Booth list
.booth-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.booth-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background-color: #F7FAFC;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  color: #1A202C;

  .booth-icon {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    background-color: #E6F4F8;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;

    &.booth-icon--scanned {
      background-color: $green-light;
      color: $green;
    }
  }
}

// Ticket rows
.detail-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 0;
  border-bottom: 1px solid #f1f5f9;

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  &:first-child {
    padding-top: 0;
  }

  &__label {
    font-size: 13px;
    color: $neutral-600;
    font-weight: 500;
  }

  &__value {
    font-size: 13px;
    color: #1A202C;
    text-align: right;
  }
}

.font-mono {
  font-family: 'Courier New', monospace;
  letter-spacing: 0.5px;
}

// Status badges
.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 11px;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 20px;
  line-height: 1.4;

  &__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  &--terdaftar {
    background: $green-light;
    color: darken($green, 10%);
    .status-badge__dot { background: $green; }
  }

  &--pending {
    background: $orange-light;
    color: darken($orange, 10%);
    .status-badge__dot { background: $orange; }
  }

  &--lunas {
    background: $blue-light;
    color: darken($blue, 5%);
    .status-badge__dot { background: $blue; }
  }

  &--belum-bayar {
    background: $red-light;
    color: darken($red, 5%);
    .status-badge__dot { background: $red; }
  }
}

// Check-in / Check-out
.checkin-timeline {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.checkin-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.checkin-icon {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  &--in {
    background: $green-light;
    color: $green;
  }

  &--out {
    background: #E6F4F8;
    color: $primary;
  }

  &--empty {
    background: #f1f5f9;
    color: $neutral-400;
  }
}

.checkin-label {
  font-size: 13px;
  font-weight: 600;
  color: #1A202C;
  line-height: 1.4;
}

.checkin-time {
  font-size: 12px;
  color: $neutral-600;
  line-height: 1.4;
  margin-top: 1px;
}

.btn-scan-booth {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  justify-content: center;
  padding: 10px 16px;
  background: $primary;
  color: #fff;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s;

  &:hover {
    background: #005F82;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.scanner-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  background: #000;
  display: flex;
  flex-direction: column;
}

.scanner-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: rgba(0, 0, 0, 0.6);
}

.scanner-title {
  color: #fff;
  font-size: 16px;
  font-weight: 600;
}

.scanner-close {
  background: none;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 4px;
  line-height: 1;
}

.scanner-viewport {
  position: relative;
  flex: 1;
  overflow: hidden;
}

.scanner-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.scanner-frame {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: '';
    display: block;
    width: 220px;
    height: 220px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-radius: 8px;
    background: transparent;
    box-shadow: 0 0 0 9999px rgba(0, 0, 0, 0.45);
  }
}

.scanner-corner {
  position: absolute;
  width: 24px;
  height: 24px;
  border-color: #fff;
  border-style: solid;

  $offset: calc(50% - 110px);

  &--tl { top: $offset; left: $offset; border-width: 3px 0 0 3px; border-radius: 4px 0 0 0; }
  &--tr { top: $offset; right: $offset; border-width: 3px 3px 0 0; border-radius: 0 4px 0 0; }
  &--bl { bottom: $offset; left: $offset; border-width: 0 0 3px 3px; border-radius: 0 0 0 4px; }
  &--br { bottom: $offset; right: $offset; border-width: 0 3px 3px 0; border-radius: 0 0 4px 0; }
}

.scanner-hint {
  color: rgba(255, 255, 255, 0.8);
  font-size: 13px;
  text-align: center;
  padding: 16px;
  margin: 0;
}

.scanner-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding-bottom: 24px;
  color: #fff;
  font-size: 14px;
}
</style>
